"use client";

import { EventItem } from "@/models/event-item";
import { Video } from "@/models/video";
import React, { Ref, forwardRef, useEffect, useRef, useState } from "react";
import styles from "./Schedule.module.css";
import moment from "moment";
import Image from "next/image";
import ReactPlayer from "react-player";
import VideoCarousel from "../VideoCarousel";
import Switch from "@mui/material/Switch";
import algoliaSearchClient, {
  algoliaVideoIndex,
} from "@/helpers/utils/algoliaConfig";
import { debounce } from "lodash";
import DisplayDate from "../../../../public/icons/date.svg";
import DisplayTime from "../../../../public/icons/time.svg";
import { toast } from "react-toastify";
import CustomToast from "../CustomToast/CustomToast";
import SpeakerProfileModal from "../speaker/profile-modal";
import { Speaker } from "@/models/speaker";
import { useGetSpeakersByEventQuery } from "@/redux/features/api-slice";
import clsx from "clsx";
import { PassAccessBadge, getPrimaryPassesForEvent } from "../PassAccess";

type ScheduleProps = {
  eventItem: EventItem;
  isMySchedule?: boolean;
  onMyScheduleClick?: (event: EventItem) => void;
  showRecommendedVideo?: boolean;
  setShowRecommendedVideo?: (show: boolean) => void;
  id: string;
  autoExpand?: boolean;
  showDescription?: boolean;
  /** Show pass access badges indicating which passes grant access */
  showPassAccess?: boolean;
};

const Schedule = forwardRef<HTMLDivElement, ScheduleProps>(
  (
    {
      eventItem: schedule,
      isMySchedule,
      onMyScheduleClick,
      showRecommendedVideo,
      setShowRecommendedVideo,
      id,
      autoExpand = false,
      showDescription = true,
      showPassAccess = false,
    }: ScheduleProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const startTimeHours = moment.unix(schedule.startTime.seconds).format("hh");
    const startTimeMinutes = moment
      .unix(schedule.startTime.seconds)
      .format("mm");
    const startTimePeriod = moment.unix(schedule.startTime.seconds).format("A");

    const endTimeHours = moment.unix(schedule.endTime.seconds).format("hh");
    const endTimeMinutes = moment.unix(schedule.endTime.seconds).format("mm");
    const endTimePeriod = moment.unix(schedule.endTime.seconds).format("A");
    const carouselRef = React.useRef<HTMLDivElement>(null);
    const endhours = moment.unix(schedule.endTime.seconds).format("hh");
    const endmin = moment.unix(schedule.endTime.seconds).format("mm");
    const endperiod = moment.unix(schedule.endTime.seconds).format("A");
    const startTimeFormatted = moment
      .unix(schedule.startTime.seconds)
      .format("MMMM D, YYYY");

    const endTimeFormatted = moment
      .unix(schedule.endTime.seconds)
      .format("MMMM D, YYYY");

    const [filteredSpeakers, setFilteredSpeakers] = useState<Speaker[]>([]);
    const [selectedSpeakers, setSelectedSpeaker] = useState<Speaker | null>(
      null
    );
    const [isProModalShow, setIsProModalShow] = useState(false);
    const { data: speakers } = useGetSpeakersByEventQuery(schedule.id);

    const vtype = `vertical-type-${schedule.vertical}`;
    const vtypo = `vertical-typo-${schedule.vertical}`;
    const [videos, setVideos] = useState<Video[]>([]);

    const pageSize = 8;

    // Add a new state variable for the page
    const [page, setPage] = useState(0);

    const [hasMoreVideos, setHasMoreVideos] = useState(true);

    useEffect(() => {
      setFilteredSpeakers(speakers || []);
    }, [speakers]);

    const fetchVideos = async () => {
      // if there are no tags, return
      if (!schedule.tags) {
        return;
      }
      // limit the number of videos to 8
      const responses = await algoliaSearchClient.search(
        schedule.tags.map((tag) => ({
          indexName: "videos",
          query: tag,
          params: {
            hitsPerPage: pageSize,
            page: page,
          },
        }))
      );

      const hits = responses.results.map((result: any) => result.hits).flat();
      const transformedHits = hits.map((hit: any) => {
        hit.id = hit.objectID;
        hit.timestamp = new Date(hit.timestamp);

        return hit as Video;
      });

      setVideos((prevVideos) => {
        const newVideos = [...prevVideos, ...transformedHits];
        const uniqueVideos = Array.from(
          new Set(newVideos.map((video) => video.id))
        )
          .map((id) => newVideos.find((video) => video.id === id))
          .filter((video) => video !== undefined) as Video[]; // Filter out undefined values
        return uniqueVideos;
      });

      if (transformedHits.length < pageSize) {
        setHasMoreVideos(false);
      }
    };

    // Add a function to load the next page
    const loadNextPage = debounce(() => {
      if (!hasMoreVideos) {
        return;
      }
      setPage((prevPage) => prevPage + 1);
      fetchVideos();
    }, 500);

    useState(() => {
      fetchVideos();
    });

    const handleMyScheduleClick = (schedule: EventItem) => {
      if (onMyScheduleClick) {
        onMyScheduleClick(schedule);
        {
          isMySchedule
            ? toast(<CustomToast message="Event removed from My Schedule!" />)
            : toast(<CustomToast message="Event added to My Schedule!" />);
        }
      }
    };

    const handleSpeakerProfile = (speaker: Speaker) => {
      if (speaker) {
        setSelectedSpeaker(speaker);
        setIsProModalShow(true);
      }
    };

    return (
      <div className="px-5 my-4 md:px-0 md:w-[49rem]">
        <div
          id={id}
          className="flex flex-col items-start max-w-[49rem] py-6 bg-[var(--Neutrals-Card-Background)] gap-3 rounded-2xl md:rounded-[1.25rem]"
          ref={ref}
        >
          <div className={styles.frame}>
            <div
              className={
                "flex flex-col md:flex-row justify-center md:justify-start gap-2"
              }
            >
              <p className={styles["time-typo"]}>
                <DisplayDate width={20} height={20} /> &nbsp;
                {startTimeFormatted}&nbsp;
              </p>

              <div
                className={clsx(styles["time-typo"], "flex items-center gap-1")}
              >
                <DisplayTime width={20} height={20} />
                {startTimeHours}:{startTimeMinutes} {startTimePeriod} -{" "}
                {endhours}:{endmin} {endperiod}
              </div>
            </div>
            <div className={styles[vtype]}>
              <p className={styles[vtypo]}>{schedule.vertical}</p>
            </div>
            {showPassAccess && (
              <PassAccessBadge
                passes={getPrimaryPassesForEvent(schedule.vertical)}
                compact
                className="ml-2"
              />
            )}
          </div>
          <div className={styles.title}>
            <p className={styles["title-typo"]}>{schedule.title}</p>
          </div>
          {showDescription && (
            <span className={styles["description-typo"]}>
              {schedule.description}
            </span>
          )}
          {showDescription &&
            filteredSpeakers &&
            filteredSpeakers.length > 0 && (
              <div
                className={
                  "grid gap-4 md:grid-cols-3 grid-cols-2 px-8 py-4 w-full"
                }
              >
                {filteredSpeakers.map((speaker, index) => (
                  <div
                    className={
                      "col-span-1 flex items-center gap-2 hover:cursor-pointer"
                    }
                    key={index}
                    onClick={() => handleSpeakerProfile(speaker)}
                  >
                    <div className="w-12 h-12 relative flex-shrink-0">
                      <Image
                        src={speaker.image}
                        alt="Display"
                        fill
                        className="rounded-full object-cover"
                      />
                    </div>

                    <div>
                      <p className="font-semibold text-sm">{speaker.name}</p>
                      <p className="text-sm">{speaker.designation}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

          {isProModalShow && selectedSpeakers ? (
            <SpeakerProfileModal
              speaker={selectedSpeakers}
              onClose={() => setIsProModalShow(false)}
              isSchedule={true}
            />
          ) : null}
          <div className="flex justify-between w-full px-8">
            {videos.length > 0 ? (
              <div className="flex items-center">
                <span className="mr-2 font-bold text-sm">
                  Recommended Video
                </span>

                <Switch
                  checked={showRecommendedVideo}
                  onChange={() => {
                    if (setShowRecommendedVideo) {
                      setShowRecommendedVideo(!showRecommendedVideo);
                    }
                  }}
                  color="secondary"
                  size={"small"}
                />
              </div>
            ) : (
              <div></div>
            )}
            <div className="flex justify-end">
              {onMyScheduleClick && isMySchedule && (
                <Image
                  src="/images/schedule-filled-heart.svg"
                  alt="arrow-right"
                  width={30}
                  height={30}
                  className="cursor-pointer"
                  onClick={() => handleMyScheduleClick(schedule)}
                />
              )}{" "}
              {onMyScheduleClick && !isMySchedule && (
                <Image
                  src="/images/schedule-unfilled-heart.svg"
                  alt="arrow-right"
                  width={30}
                  height={30}
                  className="cursor-pointer"
                  onClick={() => handleMyScheduleClick(schedule)}
                />
              )}
            </div>
          </div>

          {/* Carrousel of rectangle videos */}
          {showRecommendedVideo && (
            <div className="px-6 w-full">
              <VideoCarousel
                videos={videos}
                key={schedule.id}
                autoPlay={autoExpand}
                fetchMoreVideos={loadNextPage}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

Schedule.displayName = "Schedule";

export default Schedule;
